import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE } from "./config";
import { useLocation, useNavigate } from 'react-router-dom';
import socket, { connectSocket } from '../socket/socket';
import { resolveAvatarSource } from '../utils/avatar';

import '../css/Register.css';
import '../css/RankGuide.css';
import '../css/DailyPlayStyle.css';
import '../css/RegisterCompact.css';

const RANK_DETAILS = {
  BG: {
    description: 'ตีโต้ และดูแลตัวเองได้',
    colorClass: 'rank-bg'
  },
  BGM: {
    description: 'เซฟถึงหลัง ตบได้ และตี Rally ได้',
    colorClass: 'rank-bgm'
  },
  NB: {
    description: 'วนคอร์ทได้ ตีเหนียว ตีทน และตี Rally ได้',
    colorClass: 'rank-nb'
  },
  'NB+': {
    description: 'มีรูปเกม บุก รับ และตี Rally ได้',
    colorClass: 'rank-nb-plus'
  },
  'N-': {
    description: 'ดูแลตัวเองได้ดี และเอาตัวรอดได้',
    colorClass: 'rank-n-minus'
  },
  N: {
    description: 'บุก รับ ได้ดี',
    colorClass: 'rank-n'
  },
  'N/S': {
    description: 'ตบดี รับเหนียว ไม่มีหมด',
    colorClass: 'rank-ns'
  },
  S: {
    description: 'ตีได้หมด',
    colorClass: 'rank-s'
  },
  'S/P': {
    description: 'ซุปเปอร์',
    colorClass: 'rank-sp'
  }
};

const RANK_INITIAL_POINTS = {
  BG: 310,
  BGM: 360,
  NB: 410,
  'NB+': 460,
  'N-': 510,
  N: 560,
  'N/S': 610,
  S: 660,
  'S/P': 710
};

const RANK_ZONES = [
  {
    key: 'light-medium',
    title: 'โซน 1 • เบา–กลาง • Cardio',
    description: 'เริ่มต้น เล่นสบาย เน้นตีโต้และ Rally',
    ranks: ['BG', 'BGM']
  },
  {
    key: 'medium-heavy',
    title: 'โซน 2 • กลางไปหนัก • Aerobic',
    description: 'เริ่มมีรูปเกม บุก รับ และใช้จังหวะมากขึ้น',
    ranks: ['NB', 'NB+', 'N-']
  },
  {
    key: 'heavy',
    title: 'โซน 3 • กลาง–หนัก • Maximum',
    description: 'เกมเร็ว ตบหนัก รับเหนียว และเล่นจริงจัง',
    badge: 'Model แบดเวป',
    ranks: ['N', 'N/S', 'S', 'S/P']
  }
];

const PLAYER_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'ACTIVE — พร้อมลง' },
  { value: 'QUEUE', label: 'QUEUE — อยู่ในคิว' },
  { value: 'PLAY', label: 'PLAY — กำลังเล่น' },
  { value: 'CHECKOUT', label: 'CHECKOUT — ออกจากก๊วน' }
];

const DAILY_PLAY_STYLES = [
  {
    code: 'FAST_POWER_MIX',
    icon: '⚡',
    title: 'ทั่วไป',
    description: 'เร็ว หนัก สลับ เบา ตามระดับมือ',
    colorClass: 'play-style-fast'
  },
  {
    code: 'TIGHT_MEDIUM_HEAVY',
    icon: '🎯',
    title: 'ขอเหนื่อย',
    description: 'ตึง ปานกลาง–หนัก ตามระดับมือ',
    colorClass: 'play-style-tight'
  },
  {
    code: 'ALL_ROUND_READY',
    icon: '🔥',
    title: 'ได้หมด พร้อมชน',
    description: 'ปรับได้ทุกจังหวะ พร้อมเล่นเกมจริงจัง',
    colorClass: 'play-style-ready'
  },
  {
    code: 'ENJOY_FUN',
    icon: '😄',
    title: 'เน้นฮาและเหนื่อย',
    description: 'Enjoy เฮฮา เล่นสนุก ไม่เครียด',
    colorClass: 'play-style-enjoy'
  }
];

const NAME_CHECK_DELAY_MS = 200;

const getAvatarSource = avatarUrl => {
  return resolveAvatarSource(avatarUrl);
};

const normalizePlayerName = value => String(value || '')
  .normalize('NFKC')
  .trim()
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('th-TH');

async function readApiResponse(response) {
  const responseText = await response.text();

  if (!responseText) return {};

  try {
    return JSON.parse(responseText);
  } catch {
    const plainText = responseText
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return {
      message:
        plainText.slice(0, 250) ||
        `Backend ตอบกลับไม่ใช่ JSON (HTTP ${response.status})`
    };
  }
}

async function requestGroupNameCheck(groupId, name, signal) {
  const encodedGroupId = encodeURIComponent(groupId);
  const encodedName = encodeURIComponent(String(name || '').trim());
  const urls = [
    `${API_BASE}/api/users/check-name/${encodedGroupId}/${encodedName}`,
    `${API_BASE}/api/users/check-name/${encodedName}?groupId=${encodedGroupId}`
  ];

  for (let index = 0; index < urls.length; index += 1) {
    const response = await fetch(urls[index], { signal });
    const data = await readApiResponse(response);

    // Backend รุ่นก่อนอาจยังไม่มี Route ที่มี groupId อยู่ใน path
    if (response.status === 404 && index === 0) continue;

    return {
      response,
      data,
      groupScopeConfirmed:
        Number(data.groupId ?? data.group_id) === Number(groupId)
    };
  }

  throw new Error('ไม่พบ API ตรวจชื่อของ Group');
}

// Register.js: รายชื่อผู้เล่นอัปเดตผ่าน Socket และมี polling สำรอง
function Register({ embedded = false, initialGroupId = 0, onClose = () => {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  let loggedInUser = null;
  try {
    loggedInUser = JSON.parse(localStorage.getItem('user') || 'null');
  } catch (error) {
    loggedInUser = null;
  }
  const isAdmin = String(loggedInUser?.role || '').trim().toLowerCase() === 'admin';
  const registerParams = new URLSearchParams(location.search);
  const requestedGroupId = Number(initialGroupId || registerParams.get('groupId'));
  const qrCode = registerParams.get('code') || '';
  const qrRedirect = registerParams.get('redirect') || '';
  const isQrCheckin = registerParams.get('qrCheckin') === '1' && requestedGroupId > 0 && Boolean(qrCode);
  const isLineSignup = registerParams.get('lineSignup') === '1';
  let pendingLineRegistration = null;
  if (isLineSignup) {
    try {
      pendingLineRegistration = JSON.parse(
        sessionStorage.getItem('pending_line_registration') || 'null'
      );
    } catch (error) {
      pendingLineRegistration = null;
    }
  }
  const pendingLineTicket = String(pendingLineRegistration?.ticket || '');
  const pendingLineName = String(pendingLineRegistration?.name || '').trim();

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [form, setForm] = useState({
    name: pendingLineName,
    password: '1234',
    level_rank: '',
    daily_play_style: 'FAST_POWER_MIX'
  });
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [nameCheck, setNameCheck] = useState({
    status: 'idle',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [playerEditError, setPlayerEditError] = useState('');
  const [joiningPlayerId, setJoiningPlayerId] = useState(null);
  const [nameIdQuery, setNameIdQuery] = useState(pendingLineName);
  const [nameIdResults, setNameIdResults] = useState([]);
  const [nameIdStatus, setNameIdStatus] = useState('idle');
  const [nameIdMessage, setNameIdMessage] = useState('');
  const [showNewPlayerForm, setShowNewPlayerForm] = useState(!isAdmin || Boolean(pendingLineTicket));
  const [showRecentPlayers, setShowRecentPlayers] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [copiedPlayerId, setCopiedPlayerId] = useState(false);
  const nameCheckRequestRef = useRef(0);
  const hasExactDuplicateName = nameIdResults.some(player => (
    normalizePlayerName(player.name) === normalizePlayerName(form.name)
  ));

  useEffect(() => {
    if (!showRecentPlayers) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showRecentPlayers]);

  const fetchPlayers = useCallback(async (groupId) => {
    if (!groupId) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/users/players/${groupId}?_=${Date.now()}`,
        { cache: 'no-store' }
      );
      const data = await res.json();

      if (Array.isArray(data)) {
        setPlayers(data);
      } else if (Array.isArray(data?.data)) {
        setPlayers(data.data);
      } else {
        setPlayers([]);
      }
    } catch (err) {
      console.error("Fetch players error:", err);
      setPlayers([]);
    }
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchPlayers(selectedGroup);
    }
  }, [fetchPlayers, selectedGroup]);

  useEffect(() => {
    if (!selectedGroup) return undefined;

    const isSelectedGroupEvent = (payload = {}) => {
      const eventGroupId = payload.groupId ?? payload.group_id;

      if (eventGroupId !== undefined && eventGroupId !== null) {
        return Number(eventGroupId) === Number(selectedGroup);
      }

      const eventGroupIds = Array.isArray(payload.groupIds)
        ? payload.groupIds.map(Number)
        : [];

      // Event บางชนิด เช่น player-updated ไม่มี groupId จึงโหลด Group ปัจจุบันใหม่ได้เลย
      return eventGroupIds.length === 0 ||
        eventGroupIds.includes(Number(selectedGroup));
    };

    const refreshSelectedGroup = (payload = {}) => {
      if (!isSelectedGroupEvent(payload)) return;
      fetchPlayers(selectedGroup).catch(error => {
        console.error('Realtime players refresh error:', error);
      });
    };

    const joinAndRefresh = () => {
      socket.emit('join-group', Number(selectedGroup));
      fetchPlayers(selectedGroup).catch(error => {
        console.error('Players refresh after socket connect error:', error);
      });
    };

    socket.on('connect', joinAndRefresh);

    if (socket.connected) {
      joinAndRefresh();
    }

    socket.on('player-added', refreshSelectedGroup);
    socket.on('member-joined', refreshSelectedGroup);
    socket.on('member-updated', refreshSelectedGroup);
    socket.on('player-style-updated', refreshSelectedGroup);
    socket.on('player-status-updated', refreshSelectedGroup);
    socket.on('player-updated', refreshSelectedGroup);

    // สำรองกรณี Socket หลุดชั่วคราว เพื่อให้หน้า Register ไม่ต้องกด Refresh เอง
    const pollingId = window.setInterval(
      () => fetchPlayers(selectedGroup),
      5000
    );
    const handleFocus = () => fetchPlayers(selectedGroup);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(pollingId);
      window.removeEventListener('focus', handleFocus);
      socket.off('connect', joinAndRefresh);
      socket.off('player-added', refreshSelectedGroup);
      socket.off('member-joined', refreshSelectedGroup);
      socket.off('member-updated', refreshSelectedGroup);
      socket.off('player-style-updated', refreshSelectedGroup);
      socket.off('player-status-updated', refreshSelectedGroup);
      socket.off('player-updated', refreshSelectedGroup);
    };
  }, [fetchPlayers, selectedGroup]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    const query = nameIdQuery.trim();
    if (query.length < 2) {
      setNameIdResults([]);
      setNameIdStatus('idle');
      setNameIdMessage(query ? 'พิมพ์อย่างน้อย 2 ตัวอักษร' : '');
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setNameIdStatus('loading');
        setNameIdMessage('กำลังค้นหาอัตโนมัติ...');
        const response = await fetch(
          `${API_BASE}/api/users/find-by-name?name=${encodeURIComponent(query)}&groupId=${encodeURIComponent(selectedGroup || '')}`,
          { cache: 'no-store', signal: controller.signal }
        );
        const data = await readApiResponse(response);
        if (!response.ok) throw new Error(data.message || 'ค้นหาชื่อไม่สำเร็จ');
        const results = Array.isArray(data) ? data : data.results || [];
        setNameIdResults(results);
        setNameIdStatus('done');
        setNameIdMessage(results.length > 0 ? `พบ ${results.length} รายชื่อ` : 'ไม่พบสมาชิก • สมัครสมาชิกใหม่ได้');
      } catch (error) {
        if (error.name === 'AbortError') return;
        setNameIdResults([]);
        setNameIdStatus('error');
        setNameIdMessage(error.message || 'ค้นหาชื่อไม่สำเร็จ');
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [nameIdQuery, selectedGroup]);

  const copyRegisteredPlayerId = async () => {
    if (!registrationSuccess?.userId) return;

    const playerId = String(registrationSuccess.userId);
    try {
      await navigator.clipboard.writeText(playerId);
    } catch (error) {
      const input = document.createElement('textarea');
      input.value = playerId;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopiedPlayerId(true);
  };

  const joinExistingPlayer = async player => {
    if (!player || joiningPlayerId !== null || !selectedGroup) return;

    try {
      setJoiningPlayerId(Number(player.user_id));
      setNameIdMessage('');
      const response = await fetch(`${API_BASE}/api/users/register-existing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: player.user_id,
          group_id: selectedGroup
        })
      });
      const data = await readApiResponse(response);
      if (!response.ok) {
        throw new Error(data.message || 'ลงชื่อเข้า Group ไม่สำเร็จ');
      }
      const joinedUser = data.user || player;
      setRegistrationSuccess({
        userId: Number(joinedUser.user_id),
        name: joinedUser.name,
        message: data.message || 'ลงชื่อเข้า Group สำเร็จ'
      });
      setCopiedPlayerId(false);
      await fetchPlayers(selectedGroup);
      setNameIdResults(currentResults => currentResults.map(result => (
        Number(result.user_id) === Number(joinedUser.user_id)
          ? { ...result, alreadyInGroup: true }
          : result
      )));
      setNameIdStatus('success');
      setNameIdMessage(`ลงชื่อ ${joinedUser.name} เข้า Group สำเร็จ ✨`);
    } catch (error) {
      setNameIdStatus('error');
      setNameIdMessage(error.message || 'ลงชื่อเข้า Group ไม่สำเร็จ');
    } finally {
      setJoiningPlayerId(null);
    }
  };

  const openPlayerEditor = player => {
    const status = String(
      player.status || player.memberStatus || 'ACTIVE'
    ).trim().toUpperCase();
    const levelRank = String(player.level_rank || 'BG').trim().toUpperCase();

    setPlayerEditError('');
    setEditingPlayer({
      user_id: Number(player.user_id),
      name: String(player.name || ''),
      level_rank: levelRank,
      original_level_rank: levelRank,
      point: Number(player.point || RANK_INITIAL_POINTS[levelRank] || 310),
      status: PLAYER_STATUS_OPTIONS.some(option => option.value === status)
        ? status
        : 'ACTIVE',
      original_status: status
    });
  };

  const closePlayerEditor = () => {
    if (savingPlayer) return;
    setPlayerEditError('');
    setEditingPlayer(null);
  };

  const savePlayerEdit = async event => {
    event.preventDefault();
    if (!editingPlayer || savingPlayer || !selectedGroup) return;

    const name = editingPlayer.name.trim();
    const levelRank = String(editingPlayer.level_rank || '').trim().toUpperCase();
    const duplicatedName = players.some(player => (
      Number(player.user_id) !== Number(editingPlayer.user_id) &&
      normalizePlayerName(player.name) === normalizePlayerName(name)
    ));

    if (!name) {
      setPlayerEditError('กรุณากรอกชื่อผู้เล่น');
      return;
    }

    if (!RANK_INITIAL_POINTS[levelRank]) {
      setPlayerEditError('กรุณาเลือกระดับมือให้ถูกต้อง');
      return;
    }

    if (duplicatedName) {
      setPlayerEditError(`ชื่อ “${name}” มีอยู่แล้วใน Group นี้`);
      return;
    }

    const point = levelRank === editingPlayer.original_level_rank
      ? Number(editingPlayer.point)
      : RANK_INITIAL_POINTS[levelRank];

    try {
      setSavingPlayer(true);
      setPlayerEditError('');

      const playerResponse = await fetch(
        `${API_BASE}/api/users/${editingPlayer.user_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            level_rank: levelRank,
            point
          })
        }
      );
      const playerData = await readApiResponse(playerResponse);

      if (!playerResponse.ok) {
        throw new Error(
          playerData.message ||
          playerData.error ||
          `อัปเดตผู้เล่นไม่สำเร็จ (HTTP ${playerResponse.status})`
        );
      }

      if (editingPlayer.status !== editingPlayer.original_status) {
        const statusResponse = await fetch(
          `${API_BASE}/api/users/${editingPlayer.user_id}/group/${selectedGroup}/status`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: editingPlayer.status })
          }
        );
        const statusData = await readApiResponse(statusResponse);

        if (!statusResponse.ok) {
          throw new Error(
            statusData.message ||
            statusData.error ||
            `อัปเดตสถานะไม่สำเร็จ (HTTP ${statusResponse.status})`
          );
        }
      }

      setPlayers(currentPlayers => currentPlayers.map(player => (
        Number(player.user_id) === Number(editingPlayer.user_id)
          ? {
              ...player,
              name,
              level_rank: levelRank,
              point,
              status: editingPlayer.status,
              memberStatus: editingPlayer.status
            }
          : player
      )));
      setEditingPlayer(null);
      await fetchPlayers(selectedGroup);
    } catch (error) {
      setPlayerEditError(error.message || 'อัปเดตข้อมูลผู้เล่นไม่สำเร็จ');
    } finally {
      setSavingPlayer(false);
    }
  };

  useEffect(() => {
    const normalizedName = normalizePlayerName(form.name);
    const requestId = nameCheckRequestRef.current + 1;
    nameCheckRequestRef.current = requestId;

    if (!selectedGroup || !normalizedName) {
      setNameCheck({ status: 'idle', message: '' });
      return undefined;
    }

    const duplicateInSelectedGroup = players.some(player =>
      normalizePlayerName(player?.name) === normalizedName
    );

    if (duplicateInSelectedGroup) {
      setNameCheck({
        status: 'duplicate',
        message: 'ชื่อนี้มีอยู่ใน Group ที่เลือกแล้ว'
      });
      return undefined;
    }

    const controller = new AbortController();
    setNameCheck({
      status: 'checking',
      message: 'กำลังตรวจสอบชื่อใน Group นี้...'
    });

    const timer = window.setTimeout(async () => {
      try {
        const {
          response,
          data,
          groupScopeConfirmed
        } = await requestGroupNameCheck(
          selectedGroup,
          form.name,
          controller.signal
        );

        if (requestId !== nameCheckRequestRef.current) return;

        if (!response.ok) {
          throw new Error(data.message || `ตรวจสอบชื่อไม่สำเร็จ (HTTP ${response.status})`);
        }

        if (!groupScopeConfirmed) {
          throw new Error('Backend ไม่ได้ยืนยัน Group ที่ใช้ตรวจชื่อ');
        }

        if (data.exists || data.canCreate === false) {
          setNameCheck({
            status: 'duplicate',
            message: data.message || 'ชื่อนี้มีอยู่ใน Group นี้แล้ว'
          });
          return;
        }

        setNameCheck({
          status: 'available',
          message: 'ชื่อนี้สามารถใช้ใน Group นี้ได้'
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestId !== nameCheckRequestRef.current) return;
        console.error('CHECK GROUP NAME ERROR:', error);
        setNameCheck({
          status: 'available',
          message: 'ไม่พบชื่อซ้ำในรายชื่อ Group นี้ และระบบจะตรวจย้ำตอนสมัคร'
        });
      }
    }, NAME_CHECK_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [form.name, players, selectedGroup]);

  useEffect(() => {
    if (isAdmin && !isQrCheckin) {
      fetch(`${API_BASE}/api/groups`)
        .then(res => res.json())
        .then(data => {
          const availableGroups = Array.isArray(data) ? data : [];
          setGroups(availableGroups);
          const requestedGroup = availableGroups.find(group => Number(group.id) === requestedGroupId);
          const openGroup = availableGroups.find(group => String(group.Group_Status || '').toUpperCase() === 'OPEN');
          setSelectedGroup(requestedGroup?.id || openGroup?.id || availableGroups[0]?.id || '');
        })
        .catch(err => {
          console.error('Group fetch error:', err);
          setGroups([]);
          setSelectedGroup('');
        });
      return;
    }

    const groupUrl = isQrCheckin
      ? `${API_BASE}/api/checkin/join/${requestedGroupId}/info?code=${encodeURIComponent(qrCode)}`
      : `${API_BASE}/api/checkin/open/latest`;
    fetch(groupUrl)
      .then(res => res.json())
      .then(data => {
        if (data && data.id) {
          setGroups([data]);
          setSelectedGroup(isAdmin || isQrCheckin ? data.id : '');
        } else {
          setGroups([]);
          setSelectedGroup('');
        }
      })
      .catch(err => {
        console.error("Group fetch error:", err);
        setGroups([]);
      });
  }, [isAdmin, isQrCheckin, qrCode, requestedGroupId]);

  const handleSubmit = async e => {
    e.preventDefault();
    console.log("SUBMIT WORKING");

    if (!form.name) {
      alert("กรุณากรอกชื่อ");
      return;
    }

    if (!isAdmin && hasExactDuplicateName) {
      alert('ชื่อนี้มีผู้ใช้งานแล้ว กรุณาใช้ชื่ออื่น');
      return;
    }

    if (isQrCheckin && !selectedGroup) {
      alert("กรุณาเลือก Group");
      return;
    }

    if (!form.level_rank) {
      alert("กรุณาเลือก Rank");
      return;
    }

    if (!form.password || form.password.length < 4) {
      alert('กรุณาตั้ง Password อย่างน้อย 4 ตัวอักษร');
      return;
    }

    if (!form.daily_play_style) {
      alert("กรุณาเลือก Mood ของวันนี้");
      return;
    }

    if (nameCheck.status === 'duplicate') {
      return;
    }

    setIsSubmitting(true);
    try {
      const submittedPassword = form.password;
      const submittedName = form.name.trim();
      console.log("FORM:", form);

      if (selectedGroup) try {
        const {
          response: check,
          data: checkData,
          groupScopeConfirmed
        } = await requestGroupNameCheck(selectedGroup, form.name);

        if (
          check.ok &&
          groupScopeConfirmed &&
          (checkData.exists || checkData.canCreate === false)
        ) {
          setNameCheck({
            status: 'duplicate',
            message: checkData.message || 'ชื่อนี้มีอยู่ใน Group นี้แล้ว'
          });
          return;
        }
      } catch (checkError) {
        // POST /register ตรวจชื่อซ้ำด้วย transaction อีกครั้งอยู่แล้ว
        console.warn('PRE-REGISTER GROUP NAME CHECK:', checkError);
      }

      const res = await fetch(`${API_BASE}/api/users/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isAdmin && localStorage.getItem('token')
            ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
            : {})
        },
        body: JSON.stringify({
          ...form,
          group_id: (isAdmin || isQrCheckin) && selectedGroup
            ? Number(selectedGroup)
            : null,
          defer_checkin: isQrCheckin,
          force_create_profile: false
        })
      });

      const data = await readApiResponse(res);
      console.log("REGISTER RES:", data);

      if (!res.ok) {
        if (data.code === 'DUPLICATE_NAME_IN_GROUP') {
          setNameCheck({
            status: 'duplicate',
            message: data.message || 'ชื่อนี้มีอยู่ใน Group นี้แล้ว'
          });
          return;
        }

        throw new Error(
          data.message ||
          data.error ||
          `สมัครสมาชิกไม่สำเร็จ (HTTP ${res.status})`
        );
      }

      if (isQrCheckin) {
        const loginResponse = await fetch(`${API_BASE}/api/checkin/join/${requestedGroupId}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: qrCode,
            userId: Number(data.user_id),
            password: submittedPassword,
            deferCheckin: true
          })
        });
        const loginData = await readApiResponse(loginResponse);
        if (!loginResponse.ok) throw new Error(loginData.message || 'สมัครสำเร็จ แต่ Login เพื่อ Check-in ไม่สำเร็จ');
        localStorage.setItem('device_token', loginData.deviceToken);
        localStorage.setItem('token', loginData.token);
        localStorage.setItem('user', JSON.stringify(loginData.user));
        localStorage.setItem('last_group_id', String(requestedGroupId));
        localStorage.setItem(`pending_qr_checkin_${requestedGroupId}`, JSON.stringify(loginData.user));
        connectSocket(loginData.token);
        navigate(qrRedirect || `/join/${requestedGroupId}?code=${encodeURIComponent(qrCode)}`, {
          replace: true,
          state: { pendingCheckinUser: loginData.user }
        });
        return;
      }

      if (pendingLineTicket) {
        const lineResponse = await fetch(`${API_BASE}/api/users/line/link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticket: pendingLineTicket,
            playerId: Number(data.user_id),
            password: submittedPassword
          })
        });
        const lineData = await readApiResponse(lineResponse);
        if (!lineResponse.ok) {
          throw new Error(
            `สร้าง Player ID สำเร็จแล้ว แต่ผูก LINE ไม่สำเร็จ: ${lineData.message || 'กรุณาลอง Login LINE ใหม่'}`
          );
        }

        localStorage.setItem('token', lineData.token);
        localStorage.setItem('user', JSON.stringify(lineData.user));
        sessionStorage.removeItem('pending_line_registration');
        connectSocket(lineData.token);
        const lineReturnPath = String(lineData.returnPath || '');
        const safeLineReturnPath = lineReturnPath.startsWith('/') && !lineReturnPath.startsWith('//')
          ? lineReturnPath
          : '/profile';
        navigate(safeLineReturnPath, { replace: true });
        return;
      }

      setRegistrationSuccess({
        userId: Number(data.user_id),
        name: submittedName,
        message: data.message || 'สมัครสมาชิกสำเร็จ'
      });
      setCopiedPlayerId(false);
      await fetchPlayers(selectedGroup);
      setForm({
        name: '',
        password: '1234',
        level_rank: '',
        daily_play_style: 'FAST_POWER_MIX'
      });
      setNameIdQuery('');
      setNameIdResults([]);
      setNameIdMessage('');
      setShowNewPlayerForm(false);
    } catch (err) {
      console.error("REGISTER ERROR:", err);
      alert(`สมัครสมาชิกไม่สำเร็จ: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showRecentPlayers) {
          setShowRecentPlayers(false);
          return;
        }
        if (editingPlayer) {
          if (!savingPlayer) {
            setPlayerEditError('');
            setEditingPlayer(null);
          }
          return;
        }

        if (embedded) onClose();
        else navigate('/');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingPlayer, embedded, navigate, onClose, savingPlayer, showRecentPlayers]);

  const orderedPlayers = [...players]
    .sort((a, b) => {
      const parsedTimeA = new Date(a.checkin_time || a.created_at || 0).getTime();
      const parsedTimeB = new Date(b.checkin_time || b.created_at || 0).getTime();
      const timeA = Number.isFinite(parsedTimeA) ? parsedTimeA : 0;
      const timeB = Number.isFinite(parsedTimeB) ? parsedTimeB : 0;

      if (timeA !== timeB) return timeB - timeA;
      return Number(b.member_id || b.user_id || 0) - Number(a.member_id || a.user_id || 0);
    });
  const latestPlayerId = orderedPlayers[0]?.user_id;
  const filteredPlayers = orderedPlayers.filter(p =>
      p.name?.toLowerCase().includes(search.toLowerCase())
    );
  const displayedRecentPlayers = filteredPlayers.slice(0, 5);

  return (
    <div className={`app-layout ${embedded ? 'register-embedded' : ''}`}>
      <div className={`register-layout ${isQrCheckin ? 'qr-register-layout' : 'standard-register-layout'}`}>
        <div className="app-card register-card">
          <button
            className="back-link"
            onClick={() => embedded ? onClose() : navigate('/')}
          >
            ⬅ กลับหน้า Login
          </button>
          {!isAdmin ? (
            <button
              type="button"
              className="register-admin-login"
              onClick={() => navigate('/?redirect=/register')}
            >
              🔐 Login สำหรับ Admin
            </button>
          ) : (
            <span className="register-admin-active">🛡️ Admin: {loggedInUser?.name}</span>
          )}
        </div>

        <h2 className="app-title">🏸 สมัครสมาชิก</h2>

        {pendingLineTicket && (
          <section className="register-line-notice" role="status">
            <span aria-hidden="true">LINE</span>
            <div>
              <strong>สมัคร Player ID และผูก LINE อัตโนมัติ</strong>
              <small>หลังสมัครสำเร็จ ระบบจะเข้าสู่หน้า Profile ให้ทันที</small>
            </div>
          </section>
        )}

        <form onSubmit={handleSubmit} className="app-form">
          <div className="form-group">
            <label>Group <small>{isQrCheckin ? '(จำเป็นสำหรับ QR)' : '(ไม่บังคับ)'}</small></label>
            {(isAdmin || isQrCheckin) ? (
            <select
              value={selectedGroup}
              onChange={e => {
                nameCheckRequestRef.current += 1;
                setSelectedGroup(e.target.value);
                setPlayers([]);
                setNameCheck({ status: 'idle', message: '' });
                setNameIdResults([]);
                setNameIdStatus('idle');
                setNameIdMessage('');
                setNameIdQuery('');
                setShowNewPlayerForm(false);
                setRegistrationSuccess(null);
              }}
              required={isQrCheckin}
            >
              <option value="">
                {groups.length === 0 ? 'ยังไม่มี Group เปิดอยู่ • สมัครบัญชีอย่างเดียว' : 'ยังไม่เข้าร่วม Group ตอนนี้'}
              </option>
              {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.Group_Name}
                  </option>
              ))}
            </select>
            ) : (
              <select value="" disabled aria-label="สถานะการเข้าร่วม Group">
                <option value="">ยังไม่เข้าร่วม Group ตอนนี้</option>
              </select>
            )}
          </div>

          {registrationSuccess && (
            <section className="register-id-success" role="status" aria-live="polite">
              <span className="register-id-success-icon" aria-hidden="true">🎉</span>
              <div>
                <strong>{registrationSuccess.message}</strong>
                <p>
                  {registrationSuccess.name} — Player ID
                  <b className="registration-id-number">{registrationSuccess.userId}</b>
                </p>
                <small>เก็บ ID นี้ไว้สำหรับลงชื่อเข้า Group ใหม่และผูกบัญชี LINE</small>
              </div>
              <button type="button" onClick={copyRegisteredPlayerId}>
                {copiedPlayerId ? 'คัดลอกแล้ว ✓' : 'คัดลอก ID'}
              </button>
            </section>
          )}

          <section className="existing-player-register" aria-labelledby="existing-player-title">
            <header>
              <span aria-hidden="true">🪪</span>
              <div>
                <h3 id="existing-player-title">ค้นหาหรือสมัครผู้เล่น</h3>
                <p>พิมพ์ชื่อ ระบบจะค้นหาผู้เล่นเดิมให้อัตโนมัติ</p>
              </div>
            </header>
            <p className="register-thai-name-hint">
              🇹🇭 แนะนำให้สร้างชื่อเป็นภาษาไทยตามด้วยฉายา เพื่อง่ายต่อการค้นหาน้าา
            </p>
            <div className="existing-player-search-row">
              <input
                type="search"
                aria-label="ค้นหาหรือสมัครด้วยชื่อ"
                placeholder="พิมพ์ชื่อผู้เล่น..."
                value={nameIdQuery}
                maxLength="60"
                onChange={event => {
                  const nextName = event.target.value;
                  setNameIdQuery(nextName);
                  if (!isAdmin) {
                    setForm(current => ({ ...current, name: nextName }));
                  }
                  setNameIdResults([]);
                  setNameIdStatus('idle');
                  setNameIdMessage('');
                  setShowNewPlayerForm(!isAdmin);
                }}
              />
              <span className={`auto-search-state is-${nameIdStatus}`}>
                {nameIdStatus === 'loading' ? 'กำลังค้นหา…' : '🔍 ค้นหาอัตโนมัติ'}
              </span>
            </div>

            {nameIdMessage && (
              <p className={`name-id-message is-${nameIdStatus}`} role="status">
                {nameIdMessage}
              </p>
            )}

            {!isAdmin && hasExactDuplicateName && (
              <p className="register-name-unavailable" role="alert">
                ชื่อนี้มีผู้ใช้งานแล้ว กรุณาใช้ชื่ออื่น
              </p>
            )}

            {nameIdResults.length > 0 && (
              <div className="name-id-results">
                {nameIdResults.map(player => (
                  <article
                    key={player.user_id}
                    className={`name-id-result ${player.alreadyInGroup ? 'is-disabled' : ''}`}
                  >
                    <span className="existing-player-avatar" aria-hidden="true">
                      {player.name?.trim()?.charAt(0) || '🏸'}
                      {player.avatar_url && (
                        <img
                          src={getAvatarSource(player.avatar_url)}
                          alt=""
                          onError={event => { event.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </span>
                    <span className="name-id-result-copy">
                      <strong>{player.name}</strong>
                      <b>Player ID {player.user_id}</b>
                      <small>
                        มือ {player.level_rank || '-'} • {Number(player.point || 0)} คะแนน
                        {player.groupNames ? ` • ${player.groupNames}` : ''}
                      </small>
                    </span>
                    {isAdmin && <button
                      type="button"
                      disabled={!selectedGroup || player.alreadyInGroup || joiningPlayerId !== null}
                      onClick={() => joinExistingPlayer(player)}
                    >
                      {!selectedGroup
                        ? 'เลือก Group เพื่อลงชื่อ'
                        : player.alreadyInGroup
                        ? 'อยู่ใน Group แล้ว'
                        : joiningPlayerId === Number(player.user_id)
                          ? 'กำลังลงชื่อ…'
                          : 'ลงชื่อเข้า Group'}
                    </button>}
                  </article>
                ))}
              </div>
            )}

            {!isAdmin && nameIdQuery.trim().length >= 2 && nameIdStatus === 'done' && !hasExactDuplicateName && (
              <p className="register-name-available" role="status">
                ✓ สามารถใช้ชื่อ “{nameIdQuery.trim()}” สมัครสมาชิกได้
              </p>
            )}

            {isAdmin && nameIdQuery.trim().length >= 2 && nameIdStatus === 'done' && nameIdResults.length === 0 && !showNewPlayerForm && (
              <button
                type="button"
                className="start-new-player-button"
                onClick={() => {
                  setForm(current => ({ ...current, name: nameIdQuery.trim() }));
                  setShowNewPlayerForm(true);
                }}
              >
                ✨ ไม่พบสมาชิก — สมัคร “{nameIdQuery.trim()}” เป็นสมาชิกใหม่
              </button>
            )}
          </section>

          {showNewPlayerForm && (
          <>
          <div className="new-player-name-banner">
            <span>ผู้เล่นใหม่</span>
            <strong>{form.name || 'กรอกชื่อด้านบน'}</strong>
            {isAdmin && <button type="button" onClick={() => setShowNewPlayerForm(false)}>เปลี่ยนชื่อ</button>}
          </div>

          <div className="form-group">
            <label>Password สำหรับ Login / ผูก LINE</label>
            <input
              type="password"
              name="password"
              value={form.password}
              minLength="4"
              autoComplete="new-password"
              placeholder="ตั้งรหัสผ่านอย่างน้อย 4 ตัวอักษร"
              required
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label id="rank-label">Rank</label>
            <div
              className="rank-zone-grid"
              role="radiogroup"
              aria-labelledby="rank-label"
            >
              {RANK_ZONES.map(zone => (
                <section className={`rank-zone rank-zone-${zone.key}`} key={zone.key}>
                  <header>
                    <strong>{zone.title}</strong>
                    <small>{zone.description}</small>
                    {zone.badge && <b className="rank-zone-model">{zone.badge}</b>}
                  </header>
                  <div className="rank-zone-options">
                    {zone.ranks.map(rank => {
                      const details = RANK_DETAILS[rank];
                      return (
                        <button
                          type="button"
                          key={rank}
                          className={`rank-guide-item ${details.colorClass} ${form.level_rank === rank ? 'is-selected' : ''}`}
                          role="radio"
                          aria-checked={form.level_rank === rank}
                          onClick={() => setForm(currentForm => ({ ...currentForm, level_rank: rank }))}
                        >
                          <span className="rank-guide-badge">{rank}</span>
                          <span className="rank-guide-description">{details.description}</span>
                          <span className="rank-selected-icon" aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>

          <button
            type="submit"
            className="primary-btn"
            disabled={
              isSubmitting ||
              nameCheck.status === 'checking' ||
              nameCheck.status === 'duplicate' ||
              (!isAdmin && hasExactDuplicateName)
            }
          >
            {isSubmitting ? 'กำลังสมัคร...' : 'สมัครสมาชิก'}
          </button>
          </>
          )}

          <button
            type="button"
            className="secondary-btn"
            disabled={!selectedGroup}
            onClick={() => navigate('/monitor')}
          >
            📺 เปิด Match Monitor
          </button>
        </form>

        <div className="player-sidebar is-collapsed">
          <button
            type="button"
            className="recent-players-toggle"
            aria-expanded={showRecentPlayers}
            onClick={() => setShowRecentPlayers(true)}
          >
            <span>👥 ผู้เล่นล่าสุด</span>
            <small>แสดงสูงสุด 5 คน</small>
            <b>↗ เปิดดู</b>
          </button>
        </div>
      </div>

      {showRecentPlayers && createPortal(
        <div
          className="recent-players-modal-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setShowRecentPlayers(false);
          }}
        >
          <section
            className="recent-players-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recent-players-modal-title"
          >
            <header>
              <div>
                <h2 id="recent-players-modal-title">👥 ผู้เล่นล่าสุด</h2>
                <small>แสดงรายชื่อล่าสุดสูงสุด 5 คน</small>
              </div>
              <button type="button" aria-label="ปิดรายชื่อผู้เล่นล่าสุด" onClick={() => setShowRecentPlayers(false)}>✕</button>
            </header>

            <input
              className="player-search"
              autoFocus
              placeholder="🔍 ค้นหาผู้เล่น..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />

            <div className="player-list">
              {displayedRecentPlayers.length === 0 && (
                <div className="recent-players-empty">ไม่พบผู้เล่น</div>
              )}
              {displayedRecentPlayers.map(player => {
                const isLatest = String(player.user_id) === String(latestPlayerId);
                const playerStatus = String(
                  player.status || player.memberStatus || 'ACTIVE'
                ).trim().toUpperCase();
                return (
                  <div key={player.user_id} className={`player-item ${isLatest ? 'is-latest-player' : ''}`}>
                    <span className="player-identity">
                      <span className="player-name-line">
                        <strong>{player.name}</strong>
                        <small className="player-point">{Number(player.point || 0)} คะแนน</small>
                        {isLatest && <small className="player-latest-badge">ล่าสุด</small>}
                      </span>
                      {player.daily_play_style && (
                        <small className="player-daily-style">
                          {DAILY_PLAY_STYLES.find(style => style.code === player.daily_play_style)?.title}
                        </small>
                      )}
                    </span>
                    <span className="player-row-meta">
                      <span className="rank">{player.level_rank}</span>
                      <span className={`player-status player-status-${playerStatus.toLowerCase()}`}>{playerStatus}</span>
                    </span>
                    <button
                      type="button"
                      className="player-inline-edit"
                      title={`แก้ไข ${player.name}`}
                      aria-label={`แก้ไขข้อมูล ${player.name}`}
                      onClick={() => {
                        setShowRecentPlayers(false);
                        openPlayerEditor(player);
                      }}
                    >
                      ✏️
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </div>,
        document.body
      )}

      {editingPlayer && createPortal(
        <div
          className="register-player-editor-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) closePlayerEditor();
          }}
        >
          <form
            className="register-player-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="register-player-editor-title"
            onSubmit={savePlayerEdit}
          >
            <header className="register-player-editor-header">
              <span aria-hidden="true">✏️</span>
              <div>
                <small>PLAYER EDITOR</small>
                <h2 id="register-player-editor-title">แก้ไขข้อมูลผู้เล่น</h2>
              </div>
              <button
                type="button"
                aria-label="ปิดหน้าต่างแก้ไข"
                disabled={savingPlayer}
                onClick={closePlayerEditor}
              >
                ✕
              </button>
            </header>

            <div className="register-player-editor-fields">
              <label>
                <span>ชื่อผู้เล่น</span>
                <input
                  autoFocus
                  value={editingPlayer.name}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    name: event.target.value
                  }))}
                />
              </label>

              <label>
                <span>ระดับมือ</span>
                <select
                  value={editingPlayer.level_rank}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    level_rank: event.target.value
                  }))}
                >
                  {Object.keys(RANK_DETAILS).map(rank => (
                    <option value={rank} key={rank}>{rank}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>สถานะใน Group</span>
                <select
                  value={editingPlayer.status}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    status: event.target.value
                  }))}
                >
                  {PLAYER_STATUS_OPTIONS.map(option => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {editingPlayer.level_rank !== editingPlayer.original_level_rank && (
              <p className="register-player-editor-note">
                คะแนนจะปรับตามค่าเริ่มต้นของมือ {editingPlayer.level_rank} เป็น{' '}
                {RANK_INITIAL_POINTS[editingPlayer.level_rank]} คะแนน
              </p>
            )}

            {playerEditError && (
              <div className="register-player-editor-error" role="alert">
                ❌ {playerEditError}
              </div>
            )}

            <footer className="register-player-editor-actions">
              <button
                type="button"
                className="cancel"
                disabled={savingPlayer}
                onClick={closePlayerEditor}
              >
                ยกเลิก
              </button>
              <button type="submit" className="save" disabled={savingPlayer}>
                {savingPlayer ? 'กำลังบันทึก...' : '💾 บันทึก'}
              </button>
            </footer>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Register;
